import { computed, forwardRef, inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, switchMap } from 'rxjs';
import { startWith } from 'rxjs/operators';

import { AppData, AppRouteId } from '../../route-data';
import { useI18n } from '../i18n/services/i18n.service';
import { NavigationService } from './services/navigation.service';

export interface BackButton {
  label: string;
  action(): void | Promise<void>;
}

@Injectable({
  providedIn: 'root',
  useClass: forwardRef(() => NoopBackButtonProvider),
})
export abstract class BackButtonProvider {
  abstract readonly backButton: Signal<BackButton | undefined>;
}

@Injectable()
export class RouterBackButtonProvider extends BackButtonProvider {
  private readonly i18n = useI18n();

  private readonly navigationService = inject(NavigationService);
  private readonly router = inject(Router);

  private readonly routeData = toSignal(
    this.router.events.pipe(
      startWith(undefined),
      switchMap(() => RouterBackButtonProvider.getRoute(this.router).data as Observable<AppData>),
    ),
  );

  private readonly parentRoute = computed(() => this.routeData()?.parentRouteId);

  private labelForRoute(routeId: AppRouteId): string {
    return this.i18n.partialEnum(routeId, 'backButton', this.i18n('backButton.generic'));
  }

  readonly backButton = computed((): BackButton | undefined => {
    const previousPathData = this.navigationService.previousPathData();
    const parentRoute = this.parentRoute();

    if (previousPathData) {
      return {
        label: this.labelForRoute(previousPathData.routeId),
        action: () => this.navigationService.back(),
      };
    } else if (parentRoute) {
      return {
        label: this.labelForRoute(parentRoute),
        action: () => this.navigationService.navigateToRouteId(parentRoute, { replaceUrl: true }),
      };
    } else {
      return undefined;
    }
  });

  /** Get the route from the router, by taking the first child until there are no more children */
  private static getRoute(router: Router) {
    let route = router.routerState.root;
    while (route.firstChild) route = route.firstChild;
    return route;
  }
}

@Injectable()
export class NoopBackButtonProvider extends BackButtonProvider {
  readonly backButton = computed(() => undefined);
}
